/*
 * naipi-zoom-modal
 * Self-contained product image lightbox.
 * - Opens when a product image is clicked (delegated [data-naipi-zoom] triggers).
 * - Image centered, fit to the modal width (object-fit: contain).
 * - Zoom in/out via buttons, mouse wheel, pinch and double-click; pan when zoomed.
 * - Navigate with chevrons OR thumbnails. Identical behaviour on mobile and desktop.
 * No theme framework dependencies on purpose, so it is robust to theme updates.
 */
(function () {
  if (customElements.get('naipi-zoom-modal')) return;

  var clamp = function (v, min, max) { return Math.min(max, Math.max(min, v)); };

  class NaipiZoomModal extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.items = [];
      try {
        var dataEl = this.querySelector('[data-media]');
        if (dataEl) this.items = JSON.parse(dataEl.textContent.trim());
      } catch (e) { this.items = []; }

      this.index = 0;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.minScale = 1;
      this.maxScale = 4;

      this.refs = {
        panel: this.querySelector('[data-dialog]'),
        stage: this.querySelector('[data-stage]'),
        img: this.querySelector('[data-image]'),
        thumbs: this.querySelector('[data-thumbs]'),
        counter: this.querySelector('[data-counter]'),
        prev: this.querySelector('[data-prev]'),
        next: this.querySelector('[data-next]')
      };

      this._pointers = new Map();
      this._lastPinchDist = 0;
      this._dragStart = null;

      this._bind();
      this._buildThumbs();
      this._relocateToBody();
    }

    // Move the modal to be a direct child of <body> so it escapes any ancestor
    // stacking/transform/contain context (which would otherwise trap position:fixed
    // inside the product section and let the header show through).
    _relocateToBody() {
      try {
        if (this.parentNode === document.body) return;
        var id = this.id;
        if (id) {
          var all = document.querySelectorAll('naipi-zoom-modal');
          for (var i = 0; i < all.length; i++) {
            if (all[i] !== this && all[i].id === id && all[i].parentNode === document.body) {
              all[i].remove();
            }
          }
        }
        document.body.appendChild(this);
      } catch (e) {}
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      document.documentElement.classList.remove('naipi-zoom-open');
    }

    _bind() {
      var self = this;
      var r = this.refs;

      // Close on overlay / X / background click
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-close]')) self.close();
      });

      if (r.prev) r.prev.addEventListener('click', function (e) { e.stopPropagation(); self.go(-1); });
      if (r.next) r.next.addEventListener('click', function (e) { e.stopPropagation(); self.go(1); });

      var zin = this.querySelector('[data-zoom-in]');
      var zout = this.querySelector('[data-zoom-out]');
      var zreset = this.querySelector('[data-zoom-reset]');
      if (zin) zin.addEventListener('click', function (e) { e.stopPropagation(); self.zoomBy(1.4); });
      if (zout) zout.addEventListener('click', function (e) { e.stopPropagation(); self.zoomBy(1 / 1.4); });
      if (zreset) zreset.addEventListener('click', function (e) { e.stopPropagation(); self.resetZoom(); });

      // Wheel zoom toward cursor
      if (r.stage) {
        r.stage.addEventListener('wheel', function (e) {
          e.preventDefault();
          var c = self._cursorOffset(e.clientX, e.clientY);
          self._zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, c.x, c.y);
        }, { passive: false });

        // Double click / double tap toggles zoom
        r.stage.addEventListener('dblclick', function (e) {
          e.preventDefault();
          if (self.scale > 1) { self.resetZoom(); return; }
          var c = self._cursorOffset(e.clientX, e.clientY);
          self._zoomAt(2, c.x, c.y);
        });

        // Click empty stage area (not the image) closes
        r.stage.addEventListener('click', function (e) {
          if (e.target === r.img) return;
          if (self._dragMoved) return;
          self.close();
        });

        this._bindPointers(r.stage);
      }

      // Keyboard
      this._onKey = function (e) {
        if (self.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); self.close(); }
        else if (e.key === 'ArrowRight') { self.go(1); }
        else if (e.key === 'ArrowLeft') { self.go(-1); }
        else if (e.key === '+' || e.key === '=') { self.zoomBy(1.4); }
        else if (e.key === '-' || e.key === '_') { self.zoomBy(1 / 1.4); }
      };

      // Keep panning bounds correct after resize / image load
      window.addEventListener('resize', function () {
        self._clampPan();
        self._apply();
      });
      if (r.img) r.img.addEventListener('load', function () {
        self._clampPan();
        self._apply();
      });
    }

    _cursorOffset(clientX, clientY) {
      var rect = this.refs.img.getBoundingClientRect();
      return { x: clientX - (rect.left + rect.width / 2), y: clientY - (rect.top + rect.height / 2) };
    }

    _bindPointers(stage) {
      var self = this;

      stage.addEventListener('pointerdown', function (e) {
        if (stage.setPointerCapture) { try { stage.setPointerCapture(e.pointerId); } catch (err) {} }
        self._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        self._dragMoved = false;
        self._dragStart = { x: e.clientX, y: e.clientY, tx: self.tx, ty: self.ty, t: Date.now() };
      });

      stage.addEventListener('pointermove', function (e) {
        if (!self._pointers.has(e.pointerId)) return;
        self._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        var pts = Array.from(self._pointers.values());

        // Pinch zoom
        if (pts.length === 2) {
          var dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          if (self._lastPinchDist) {
            var midx = (pts[0].x + pts[1].x) / 2;
            var midy = (pts[0].y + pts[1].y) / 2;
            var rect = self.refs.img.getBoundingClientRect();
            self._zoomAt(dist / self._lastPinchDist, midx - (rect.left + rect.width / 2), midy - (rect.top + rect.height / 2));
          }
          self._lastPinchDist = dist;
          self._dragMoved = true;
          return;
        }

        // Single pointer: pan when zoomed
        if (!self._dragStart) return;
        var dx = e.clientX - self._dragStart.x;
        var dy = e.clientY - self._dragStart.y;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) self._dragMoved = true;
        if (self.scale > 1) {
          self.tx = self._dragStart.tx + dx;
          self.ty = self._dragStart.ty + dy;
          self._clampPan();
          self._apply();
        }
      });

      var end = function (e) {
        // Swipe to change image when not zoomed
        if (self.scale <= 1 && self._dragStart && self._pointers.size === 1) {
          var dx = e.clientX - self._dragStart.x;
          var dt = Date.now() - self._dragStart.t;
          if (Math.abs(dx) > 50 && dt < 600) self.go(dx < 0 ? 1 : -1);
        }
        self._pointers.delete(e.pointerId);
        if (self._pointers.size < 2) self._lastPinchDist = 0;
        if (self._pointers.size === 0) self._dragStart = null;
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', end);
    }

    _buildThumbs() {
      var self = this;
      var t = this.refs.thumbs;
      if (!t) return;
      t.innerHTML = '';
      if (this.items.length <= 1) { t.style.display = 'none'; return; }
      this.items.forEach(function (it, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'naipi-zoom__thumb';
        b.setAttribute('aria-label', 'Image ' + (i + 1));
        var im = document.createElement('img');
        im.src = it.thumb;
        im.alt = it.alt || '';
        im.loading = 'lazy';
        b.appendChild(im);
        b.addEventListener('click', function (e) { e.stopPropagation(); self.goTo(i); });
        t.appendChild(b);
      });
    }

    open(index) {
      if (!this.items.length) return;
      this.index = clamp(index || 0, 0, this.items.length - 1);
      this.hidden = false;
      document.documentElement.classList.add('naipi-zoom-open');
      document.addEventListener('keydown', this._onKey);
      this._render();
      if (this.refs.panel && this.refs.panel.focus) this.refs.panel.focus();
    }

    close() {
      this.hidden = true;
      document.documentElement.classList.remove('naipi-zoom-open');
      document.removeEventListener('keydown', this._onKey);
      this.resetZoom();
    }

    go(delta) { this.goTo(this.index + delta); }

    goTo(i) {
      var n = this.items.length;
      if (!n) return;
      this.index = ((i % n) + n) % n;
      this.resetZoom();
      this._render();
    }

    _render() {
      var it = this.items[this.index];
      if (!it) return;
      var img = this.refs.img;
      if (img) { img.src = it.full; img.alt = it.alt || ''; }
      if (this.refs.counter) this.refs.counter.textContent = (this.index + 1) + ' / ' + this.items.length;

      var thumbs = this.refs.thumbs ? this.refs.thumbs.children : [];
      for (var i = 0; i < thumbs.length; i++) {
        var active = i === this.index;
        thumbs[i].setAttribute('aria-selected', String(active));
        if (active && thumbs[i].scrollIntoView) {
          thumbs[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        }
      }

      var single = this.items.length <= 1;
      if (this.refs.prev) this.refs.prev.style.display = single ? 'none' : '';
      if (this.refs.next) this.refs.next.style.display = single ? 'none' : '';
      this._apply();
    }

    zoomBy(factor) { this._zoomAt(factor, 0, 0); }

    _zoomAt(factor, cx, cy) {
      var prev = this.scale;
      var next = clamp(prev * factor, this.minScale, this.maxScale);
      if (next === prev) {
        if (next === 1) { this.tx = 0; this.ty = 0; this._apply(); }
        return;
      }
      var ratio = next / prev;
      // Keep the point under the cursor stationary
      this.tx = (this.tx - cx) * ratio + cx;
      this.ty = (this.ty - cy) * ratio + cy;
      this.scale = next;
      if (this.scale <= 1.001) { this.scale = 1; this.tx = 0; this.ty = 0; }
      this._clampPan();
      this._apply();
    }

    resetZoom() { this.scale = 1; this.tx = 0; this.ty = 0; this._apply(); }

    _clampPan() {
      var img = this.refs.img, stage = this.refs.stage;
      if (!img || !stage) return;
      var sr = stage.getBoundingClientRect();
      var iw = img.clientWidth * this.scale;
      var ih = img.clientHeight * this.scale;
      var maxX = Math.max(0, (iw - sr.width) / 2);
      var maxY = Math.max(0, (ih - sr.height) / 2);
      this.tx = clamp(this.tx, -maxX, maxX);
      this.ty = clamp(this.ty, -maxY, maxY);
    }

    _apply() {
      var img = this.refs.img;
      if (!img) return;
      img.style.transform = 'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.scale + ')';
      img.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in';
      this.classList.toggle('is-zoomed', this.scale > 1);
    }
  }

  customElements.define('naipi-zoom-modal', NaipiZoomModal);

  // Delegated open: works across variant swaps / re-rendered galleries.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-naipi-zoom]');
    if (!trigger) return;
    var sel = trigger.getAttribute('data-naipi-zoom');
    var modal = sel ? document.querySelector(sel) : null;
    if (!modal || typeof modal.open !== 'function') return;
    e.preventDefault();
    var idx = parseInt(trigger.getAttribute('data-naipi-index') || '0', 10);
    modal.open(isNaN(idx) ? 0 : idx);
  });
})();
